import NProgress from "nprogress";
import { createRouter, createWebHistory } from "vue-router";
import Index from "./views/Admin/Index";
import Settings from "./views/Admin/Settings";
import VideoInstructions from "./views/Admin/VideoInstructions";
import Admin from "./components/Admin";

import ClientClient from "./components/Client";
import ClientIndex from "./views/Client/Index";
import ClientEngraving from "./views/Client/Engraving";
import ClientOrder from "./views/Client/Order";
import Confirm from "./views/Client/Confirm";
import ClientOrderSuccess from "./views/Client/OrderSuccess";

//Materials
import List from "./views/Admin/Materials/List";
import Add from "./views/Admin/Materials/Add";
import Edit from "./views/Admin/Materials/Edit";

//Elements
import ListElements from "./views/Admin/Elements/List";
import FormElement from "./views/Admin/Elements/Form";
import ElementsTypesList from "./views/Admin/Elements/TypesList";
import ElementsTypesForm from "./views/Admin/Elements/TypesForm";

//Engraving
import ListEngraving from "./views/Admin/Engraving/List";
import FormEngraving from "./views/Admin/Engraving/Form";
import EngravingTypesList from "./views/Admin/Engraving/TypesList";
import EngravingTypesForm from "./views/Admin/Engraving/TypesForm";

//Engraving
import ListLandscaping from "./views/Admin/Landscaping/List";
import FormLandscaping from "./views/Admin/Landscaping/Form";
import LandscapingTypesList from "./views/Admin/Landscaping/TypesList";
import LandscapingTypesForm from "./views/Admin/Landscaping/TypesForm";

//Plots
import ListPlots from "./views/Admin/Plots/List";
import FormPlot from "./views/Admin/Plots/Form";

//Faces
import ListFaces from "./views/Admin/Faces/List";
import FormFace from "./views/Admin/Faces/Form";

//Pages
import ListPages from "./views/Admin/Pages/List";
import FormPage from "./views/Admin/Pages/Form";

//Pages
import ListOrders from "./views/Admin/Orders/List";
import DetailOrder from "./views/Admin/Orders/Detail";

//Barriers
import ListBarriers from "./views/Admin/Barriers/List";
import FormBarrier from "./views/Admin/Barriers/Form";

import Services from "./views/Admin/Services.vue";
import About from "./views/Admin/About.vue";

import EngravingText from "./views/Admin/Engraving/Text";
import AdminSearch from "./views/Admin/Search";
import License from "./views/Admin/License";
import Updates from "./views/Admin/Updates";
import NoLicense from "./views/Admin/NoLicense";
import NotFound from "./views/Error/NotFound";

import store from "./store";

const getRouter = (settings) => {
    const admin_path = settings?.admin_path || "admin";
    const scrollPositions = new Map();
    const isAdminRoute = (route) => {
        const path = route?.path ?? "";
        return path === `/${admin_path}` || path.startsWith(`/${admin_path}/`);
    };

    const router = createRouter({
        history: createWebHistory(),
        scrollBehavior(to, from, savedPosition) {
            if (savedPosition) return savedPosition;

            if (!isAdminRoute(to)) return { left: 0, top: 0 };

            const toName = String(to?.name ?? "");
            const shouldForceTop =
                /(^|_)(edit|detail|add)($|_)/i.test(toName) ||
                /\/(edit|add)$/.test(to?.path ?? "");

            if (shouldForceTop) return { left: 0, top: 0 };

            const storedPosition = scrollPositions.get(to.fullPath);
            if (storedPosition) return storedPosition;

            return { left: 0, top: 0 };
        },
        routes: [
            {
                path: "/",
                component: ClientClient,

                children: [
                    {
                        path: "",
                        component: ClientIndex,
                        name: "index",
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch("settings/fetchSettingImages");
                            next();
                        },
                    },
                    {
                        path: "/engraving",
                        component: ClientEngraving,
                        name: "engraving",
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch("settings/fetchSettingImages");
                            await store.dispatch("clientEngravings/fetch");
                            next();
                        },
                    },
                    { path: "/order", component: ClientOrder, name: "order" },
                    {
                        path: "/order-success/:order_id",
                        component: ClientOrderSuccess,
                        name: "order-success",
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            routeTo.params.order = await store.dispatch(
                                "clientOrder/fetchOrder",
                                routeTo.params.order_id
                            );
                            next();
                        },
                    },
                ],
            },
            {
                path: "/confirm",
                component: Confirm,
                name: "Confirm",
                async beforeEnter(routeTo, routeFrom, next) {
                    await store.dispatch("settings/fetchSettingImages");
                    next();
                },
            },

            {
                path: `/${admin_path}`,
                component: Admin,
                children: [
                    { path: "", name: "home", component: Index },
                    {
                        path: "no-license",
                        name: "no_license",
                        component: NoLicense,
                    },

                    { path: "about", name: "about", component: About },

                    //Material
                    {
                        path: "materials",
                        name: "materials",
                        component: List,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const perPage = parseInt(
                                routeTo.query?.perPage ??
                                    store.state.settings.settings?.per_page ??
                                    25
                            );
                            const page = parseInt(routeTo.query?.page ?? 1);
                            store.commit("materials/SET_PER_PAGE", perPage);
                            store.commit("materials/SET_PAGE", page);
                            await store.dispatch("materials/fetch");
                            await store.dispatch(
                                "materials/fetchMainMaterials"
                            );

                            await store.dispatch(
                                "types/setCurrentModelAndGroupId",
                                {
                                    currentModel: "App\\Models\\Material",
                                    currentGroupId: 0,
                                }
                            );

                            await store.dispatch("types/fetch");
                            next();
                        },
                    },
                    {
                        path: "materials/add",
                        name: "add_material",
                        component: Add,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch("materials/fetchTypes");
                            next();
                        },
                    },
                    {
                        path: "materials/:material_id/edit",
                        name: "edit_material",
                        component: Edit,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch("materials/fetchTypes");
                            routeTo.params.material = await store.dispatch(
                                "materials/fetchMaterial",
                                routeTo.params.material_id
                            );
                            next();
                        },
                    },

                    //Elements
                    {
                        path: "elements/:slug",
                        name: "elements",
                        component: ListElements,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            try {
                                const element_group = await store.dispatch(
                                    "elements/fetchElementGroup",
                                    routeTo.params.slug
                                );
                                const perPage = parseInt(
                                    routeTo.query?.perPage ??
                                        store.state.settings.settings?.per_page ??
                                        25
                                );
                                const page = parseInt(routeTo.query?.page ?? 1);
                                store.commit("elements/SET_PER_PAGE", perPage);
                                store.commit("elements/SET_PAGE", page);
                                if (routeTo.query?.type) {
                                    store.commit(
                                        "elements/SET_TYPE",
                                        parseInt(routeTo.query.type)
                                    );
                                }
                                await store.dispatch("elements/fetch");

                                await store.dispatch(
                                    "types/setCurrentModelAndGroupId",
                                    {
                                        currentModel: "App\\Models\\Element",
                                        currentGroupId:
                                            element_group &&
                                            element_group.data.element_group_id,
                                    }
                                );

                                await store.dispatch("types/fetch");
                                await store.dispatch("types/fetchAll");
                                next();
                            } catch (error) {
                                store.dispatch(
                                    "notification/add",
                                    {
                                        type: "error",
                                        message:
                                            error?.response?.data?.message ||
                                            error?.message ||
                                            "Ошибка загрузки списка элементов",
                                    },
                                    { root: true }
                                );
                                next({ name: "home" });
                            }
                        },
                    },
                    {
                        path: "elements/:slug/types",
                        name: "elements_types",
                        component: ElementsTypesList,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch("types/setCurrentModelAndGroupId", {
                                currentModel: "App\\Models\\Element",
                                currentGroupId:
                                    element_group && element_group.data.element_group_id,
                            });

                            store.dispatch("types/setAdd", false);
                            store.dispatch("types/setEdit", null);
                            store.commit("types/FETCH_TYPE", {});

                            await store.dispatch("types/fetch");
                            await store.dispatch("types/fetchAll");
                            next();
                        },
                    },
                    {
                        path: "elements/:slug/types/add",
                        name: "add_element_type",
                        component: ElementsTypesForm,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch("types/setCurrentModelAndGroupId", {
                                currentModel: "App\\Models\\Element",
                                currentGroupId:
                                    element_group && element_group.data.element_group_id,
                            });

                            store.dispatch("types/setAdd", false);
                            store.dispatch("types/setEdit", null);
                            store.commit("types/FETCH_TYPE", {});
                            next();
                        },
                    },
                    {
                        path: "elements/:slug/types/:type_id/edit",
                        name: "edit_element_type",
                        component: ElementsTypesForm,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch("types/setCurrentModelAndGroupId", {
                                currentModel: "App\\Models\\Element",
                                currentGroupId:
                                    element_group && element_group.data.element_group_id,
                            });

                            store.dispatch("types/setAdd", false);
                            store.dispatch("types/setEdit", null);
                            await store.dispatch("types/fetchType", routeTo.params.type_id);
                            next();
                        },
                    },
                    {
                        path: "elements/:slug/add",
                        name: "add_element",
                        component: FormElement,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            try {
                                await store.dispatch(
                                    "elements/clearElementSizesAndMaterials"
                                );

                                const element_group = await store.dispatch(
                                    "elements/fetchElementGroup",
                                    routeTo.params.slug
                                );

                                routeTo.params.element_group = element_group;

                                await store.dispatch(
                                    "elements/fetchTypes",
                                    routeTo.params.slug
                                );

                                await store.dispatch(
                                    "elements/fetchMainMaterials"
                                );

                                await store.dispatch("materials/getAll");

                                next();
                            } catch (error) {
                                store.dispatch(
                                    "notification/add",
                                    {
                                        type: "error",
                                        message:
                                            error?.response?.data?.message ||
                                            error?.message ||
                                            "Ошибка загрузки данных",
                                    },
                                    { root: true }
                                );
                                next({ name: "home" });
                            }
                        },
                    },
                    {
                        path: "elements/:slug/:element_id/edit",
                        name: "edit_element",
                        component: FormElement,
                        props: true,

                        async beforeEnter(routeTo, routeFrom, next) {
                            try {
                                const element_group = await store.dispatch(
                                    "elements/fetchElementGroup",
                                    routeTo.params.slug
                                );
                                routeTo.params.element_group = element_group;

                                await store.dispatch(
                                    "elements/fetchTypes",
                                    routeTo.params.slug
                                );

                                routeTo.params.element = await store.dispatch(
                                    "elements/fetchElement",
                                    {
                                        element_group_slug: routeTo.params.slug,
                                        element_id: routeTo.params.element_id,
                                    }
                                );

                                await store.dispatch("materials/getAll");

                                next();
                            } catch (error) {
                                store.dispatch(
                                    "notification/add",
                                    {
                                        type: "error",
                                        message:
                                            error?.response?.data?.message ||
                                            error?.message ||
                                            "Ошибка загрузки элемента",
                                    },
                                    { root: true }
                                );
                                next({ name: "home" });
                            }
                        },
                    },
                    {
                        path: "settings",
                        name: "settings",
                        component: Settings,
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch("settings/fetchSettingImages");
                            next();
                        },
                    },
                    {
                        path: "video-instructions",
                        name: "videoInstructions",
                        component: VideoInstructions,
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch("videoInstructions/fetch");
                            next();
                        },
                    },
                    {
                        path: "services",
                        name: "services",
                        component: Services,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch("fetchLicense");
                            next();
                        },
                    },

                    //Plots
                    {
                        path: "plots",
                        name: "plots",
                        component: ListPlots,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const perPage = parseInt(
                                routeTo.query?.perPage ??
                                    store.state.settings.settings?.per_page ??
                                    25
                            );
                            const page = parseInt(routeTo.query?.page ?? 1);
                            store.commit("plots/SET_PER_PAGE", perPage);
                            store.commit("plots/SET_PAGE", page);
                            await store.dispatch("plots/fetch");
                            next();
                        },
                    },
                    {
                        path: "plots/add",
                        name: "add_plot",
                        component: FormPlot,
                        beforeEnter(routeTo, routeFrom, next) {
                            next();
                        },
                    },
                    {
                        path: "plots/:plot_id/edit",
                        name: "edit_plot",
                        component: FormPlot,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            routeTo.params.plot = await store.dispatch(
                                "plots/fetchPlot",
                                routeTo.params.plot_id
                            );
                            next();
                        },
                    },

                    //Faces
                    {
                        path: "faces",
                        name: "faces",
                        component: ListFaces,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const perPage = parseInt(
                                routeTo.query?.perPage ??
                                    store.state.settings.settings?.per_page ??
                                    25
                            );
                            const page = parseInt(routeTo.query?.page ?? 1);
                            store.commit("faces/SET_PER_PAGE", perPage);
                            store.commit("faces/SET_PAGE", page);
                            await store.dispatch("faces/fetch");
                            next();
                        },
                    },
                    {
                        path: "faces/add",
                        name: "add_face",
                        component: FormFace,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            routeTo.params.groups = await store.dispatch(
                                "faces/getGroups"
                            );
                            next();
                        },
                    },
                    {
                        path: "faces/:id/edit",
                        name: "edit_face",
                        component: FormFace,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            routeTo.params.face = await store.dispatch(
                                "faces/fetchFace",
                                routeTo.params.id
                            );
                            routeTo.params.groups = await store.dispatch(
                                "faces/getGroups"
                            );
                            next();
                        },
                    },

                    //Pages
                    {
                        path: "pages",
                        name: "pages",
                        component: ListPages,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const perPage = parseInt(
                                routeTo.query?.perPage ??
                                    store.state.settings.settings?.per_page ??
                                    25
                            );
                            const page = parseInt(routeTo.query?.page ?? 1);
                            store.commit("pages/SET_PER_PAGE", perPage);
                            store.commit("pages/SET_PAGE", page);
                            await store.dispatch("pages/fetch");
                            next();
                        },
                    },
                    {
                        path: "pages/add",
                        name: "add_page",
                        component: FormPage,
                    },
                    {
                        path: "pages/:page_id/edit",
                        name: "edit_page",
                        component: FormPage,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            routeTo.params.page = await store.dispatch(
                                "pages/fetchPage",
                                routeTo.params.page_id
                            );
                            next();
                        },
                    },

                    //Barriers
                    {
                        path: "barriers",
                        name: "barriers",
                        component: ListBarriers,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const perPage = parseInt(
                                routeTo.query?.perPage ??
                                    store.state.settings.settings?.per_page ??
                                    25
                            );
                            const page = parseInt(routeTo.query?.page ?? 1);
                            store.commit("barriers/SET_PER_PAGE", perPage);
                            store.commit("barriers/SET_PAGE", page);
                            await store.dispatch(
                                "types/setCurrentModelAndGroupId",
                                {
                                    currentModel: "App\\Models\\Barrier",
                                    currentGroupId: null,
                                }
                            );
                            await store.dispatch("types/fetch");
                            await store.dispatch("types/fetchAll");
                            await store.dispatch("barriers/fetch");
                            next();
                        },
                    },
                    {
                        path: "barriers/add",
                        name: "add_barrier",
                        component: FormBarrier,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch("barriers/fetchTypes");
                            next();
                        },
                    },
                    {
                        path: "barriers/:barrier_id/edit",
                        name: "edit_barrier",
                        component: FormBarrier,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch("barriers/fetchTypes");
                            routeTo.params.barrier = await store.dispatch(
                                "barriers/fetchBarrier",
                                routeTo.params.barrier_id
                            );
                            next();
                        },
                    },
                    {
                        path: "licence",
                        name: "licence",
                        component: License,
                    },

                    {
                        path: "updates",
                        name: "updates",
                        component: Updates,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            next();
                        },
                    },
                    {
                        path: "orders",
                        name: "orders",
                        component: ListOrders,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const perPage = parseInt(
                                routeTo.query?.perPage ??
                                    store.state.settings.settings?.per_page ??
                                    25
                            );
                            const page = parseInt(routeTo.query?.page ?? 1);
                            store.commit("orders/SET_PER_PAGE", perPage);
                            store.commit("orders/SET_PAGE", page);
                            await store.dispatch("orders/fetch");
                            next();
                        },
                    },
                    {
                        path: "orders/:order_id",
                        name: "order_detail",
                        component: DetailOrder,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            routeTo.params.order = await store.dispatch(
                                "orders/fetchOrder",
                                routeTo.params.order_id
                            );
                            next();
                        },
                    },
                    {
                        path: "appearance",
                        name: "appearance",
                        component: Index,
                    },
                    {
                        path: "engraving-text",
                        name: "engraving-text",
                        component: EngravingText,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            routeTo.params.fonts = await store.dispatch(
                                "fonts/fetch"
                            );
                            routeTo.params.settings = await store.dispatch(
                                "settings/fetch"
                            );
                            next();
                        },
                    },

                    //Engravings
                    {
                        path: "engravings/:slug",
                        name: "engravings",
                        component: ListEngraving,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch(
                                "types/setCurrentModelAndGroupId",
                                {
                                    currentModel: "App\\Models\\Element",
                                    currentGroupId:
                                        element_group &&
                                        element_group.data.element_group_id,
                                }
                            );

                            await store.dispatch("types/fetch");
                            await store.dispatch("types/fetchAll");
                            next();
                        },
                    },
                    {
                        path: "engravings/:slug/types",
                        name: "engravings_types",
                        component: EngravingTypesList,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch("types/setCurrentModelAndGroupId", {
                                currentModel: "App\\Models\\Element",
                                currentGroupId:
                                    element_group && element_group.data.element_group_id,
                            });

                            store.dispatch("types/setAdd", false);
                            store.dispatch("types/setEdit", null);
                            store.commit("types/FETCH_TYPE", {});

                            await store.dispatch("types/fetch");
                            await store.dispatch("types/fetchAll");
                            next();
                        },
                    },
                    {
                        path: "engravings/:slug/types/add",
                        name: "add_engraving_type",
                        component: EngravingTypesForm,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch("types/setCurrentModelAndGroupId", {
                                currentModel: "App\\Models\\Element",
                                currentGroupId:
                                    element_group && element_group.data.element_group_id,
                            });

                            store.dispatch("types/setAdd", false);
                            store.dispatch("types/setEdit", null);
                            store.commit("types/FETCH_TYPE", {});
                            next();
                        },
                    },
                    {
                        path: "engravings/:slug/types/:type_id/edit",
                        name: "edit_engraving_type",
                        component: EngravingTypesForm,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch("types/setCurrentModelAndGroupId", {
                                currentModel: "App\\Models\\Element",
                                currentGroupId:
                                    element_group && element_group.data.element_group_id,
                            });

                            store.dispatch("types/setAdd", false);
                            store.dispatch("types/setEdit", null);
                            await store.dispatch("types/fetchType", routeTo.params.type_id);
                            next();
                        },
                    },
                    {
                        path: "engravings/:slug/add",
                        name: "add_engraving",
                        component: FormEngraving,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch(
                                "elements/clearElementSizesAndMaterials"
                            );
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );
                            routeTo.params.element_group = element_group;
                            await store.dispatch(
                                "elements/fetchTypes",
                                routeTo.params.slug
                            );
                            await store.dispatch("elements/fetchMainMaterials");
                            await store.dispatch("materials/fetch");
                            next();
                        },
                    },
                    {
                        path: "engravings/:slug/:element_id/edit",
                        name: "edit_engraving",
                        component: FormEngraving,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );
                            routeTo.params.element_group = element_group;

                            await store.dispatch(
                                "elements/fetchTypes",
                                routeTo.params.slug
                            );

                            routeTo.params.element = await store.dispatch(
                                "elements/fetchElement",
                                {
                                    element_group_slug: routeTo.params.slug,
                                    element_id: routeTo.params.element_id,
                                }
                            );

                            await store.dispatch("materials/fetch");

                            next();
                        },
                    },

                    //Landscaping
                    {
                        path: "landscaping/:slug",
                        name: "landscapings",
                        component: ListLandscaping,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch(
                                "types/setCurrentModelAndGroupId",
                                {
                                    currentModel: "App\\Models\\Element",
                                    currentGroupId:
                                        element_group &&
                                        element_group.data.element_group_id,
                                }
                            );

                            await store.dispatch("types/fetch");
                            await store.dispatch("types/fetchAll");
                            next();
                        },
                    },
                    {
                        path: "landscaping/:slug/types",
                        name: "landscaping_types",
                        component: LandscapingTypesList,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch("types/setCurrentModelAndGroupId", {
                                currentModel: "App\\Models\\Element",
                                currentGroupId:
                                    element_group && element_group.data.element_group_id,
                            });

                            store.dispatch("types/setAdd", false);
                            store.dispatch("types/setEdit", null);
                            store.commit("types/FETCH_TYPE", {});

                            await store.dispatch("types/fetch");
                            await store.dispatch("types/fetchAll");
                            next();
                        },
                    },
                    {
                        path: "landscaping/:slug/types/add",
                        name: "add_landscaping_type",
                        component: LandscapingTypesForm,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch("types/setCurrentModelAndGroupId", {
                                currentModel: "App\\Models\\Element",
                                currentGroupId:
                                    element_group && element_group.data.element_group_id,
                            });

                            store.dispatch("types/setAdd", false);
                            store.dispatch("types/setEdit", null);
                            store.commit("types/FETCH_TYPE", {});
                            next();
                        },
                    },
                    {
                        path: "landscaping/:slug/types/:type_id/edit",
                        name: "edit_landscaping_type",
                        component: LandscapingTypesForm,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );

                            await store.dispatch("types/setCurrentModelAndGroupId", {
                                currentModel: "App\\Models\\Element",
                                currentGroupId:
                                    element_group && element_group.data.element_group_id,
                            });

                            store.dispatch("types/setAdd", false);
                            store.dispatch("types/setEdit", null);
                            await store.dispatch("types/fetchType", routeTo.params.type_id);
                            next();
                        },
                    },
                    {
                        path: "landscaping/:slug/add",
                        name: "add_landscaping",
                        component: FormLandscaping,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            await store.dispatch(
                                "elements/clearElementSizesAndMaterials"
                            );
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );
                            routeTo.params.element_group = element_group;
                            await store.dispatch(
                                "elements/fetchTypes",
                                routeTo.params.slug
                            );
                            await store.dispatch("elements/fetchMainMaterials");
                            await store.dispatch("materials/fetch");
                            next();
                        },
                    },
                    {
                        path: "landscaping/:slug/:element_id/edit",
                        name: "edit_landscaping",
                        component: FormLandscaping,
                        props: true,
                        async beforeEnter(routeTo, routeFrom, next) {
                            const element_group = await store.dispatch(
                                "elements/fetchElementGroup",
                                routeTo.params.slug
                            );
                            routeTo.params.element_group = element_group;

                            await store.dispatch(
                                "elements/fetchTypes",
                                routeTo.params.slug
                            );

                            routeTo.params.element = await store.dispatch(
                                "elements/fetchElement",
                                {
                                    element_group_slug: routeTo.params.slug,
                                    element_id: routeTo.params.element_id,
                                }
                            );

                            await store.dispatch("materials/fetch");

                            next();
                        },
                    },
                    {
                        path: "search",
                        name: "AdminSearch",
                        component: AdminSearch,
                    },
                ],
            },

            { path: "/:pathMatch(.*)*", name: "NotFound", component: NotFound },
        ],
    });

    router.beforeEach(async (routeTo, routeFrom, next) => {
        try {
            if (
                typeof window !== "undefined" &&
                routeFrom?.fullPath &&
                isAdminRoute(routeFrom)
            ) {
                scrollPositions.set(routeFrom.fullPath, {
                    left: window.scrollX ?? 0,
                    top: window.scrollY ?? 0,
                });

                while (scrollPositions.size > 100) {
                    scrollPositions.delete(scrollPositions.keys().next().value);
                }
            }

            NProgress.start();
            await store.dispatch("fetchLicense");
            await store.dispatch("clientOrder/getComplectation", {
                order_cache: routeTo.query.order_cache,
                load_element_id: routeTo.query.load_element_id,
                load_barrier_id: routeTo.query.load_barrier_id,
            });
            next();
        } catch (error) {
            store.dispatch(
                "notification/add",
                {
                    type: "error",
                    message:
                        error?.response?.data?.message ||
                        error?.message ||
                        "Ошибка навигации",
                },
                { root: true }
            );
            next(false);
        }
    });
    router.afterEach(() => {
        NProgress.done();
    });

    router.onError((error) => {
        store.dispatch(
            "notification/add",
            {
                type: "error",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Ошибка маршрутизации",
            },
            { root: true }
        );
        NProgress.done();
    });

    return router;
};

export default getRouter;
